/**
 * FriendsPage Component
 * Page for managing friends - view friends, send/accept requests, search users
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getFriends,
  getFriendRequests,
  searchUsers,
  sendFriendRequest,
  acceptFriendRequest,
  rejectFriendRequest,
  removeFriend,
  type Friend,
  type FriendRequest,
  type PublicUser,
} from '../api';
import { useAuth } from '../contexts/AuthContext';
import UsernameSetupModal from './UsernameSetupModal';

export default function FriendsPage() {
  const { hasUsername } = useAuth();
  const [friends, setFriends] = useState<Friend[]>([]);
  const [requests, setRequests] = useState<FriendRequest[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<PublicUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showUsernameModal, setShowUsernameModal] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [friendsData, requestsData] = await Promise.all([getFriends(), getFriendRequests()]);
      setFriends(friendsData);
      setRequests(requestsData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load friends');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const searchTimeout = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await searchUsers(searchQuery.trim());
        // Filter out users who are already friends
        const friendIds = new Set(friends.map((f) => f.id));
        setSearchResults(results.filter((u) => !friendIds.has(u.id)));
      } catch {
        // Ignore search errors
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [searchQuery, friends]);

  const handleSendRequest = async (username: string) => {
    if (!hasUsername) {
      setShowUsernameModal(true);
      return;
    }

    setActionError(null);
    try {
      await sendFriendRequest(username);
      setSearchQuery('');
      setSearchResults([]);
      // Show success message or reload
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to send friend request');
    }
  };

  const handleAcceptRequest = async (requestId: number) => {
    setActionError(null);
    try {
      await acceptFriendRequest(requestId);
      await loadData();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to accept request');
    }
  };

  const handleRejectRequest = async (requestId: number) => {
    setActionError(null);
    try {
      await rejectFriendRequest(requestId);
      setRequests((prev) => prev.filter((r) => r.id !== requestId));
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to reject request');
    }
  };

  const handleRemoveFriend = async (friendshipId: number) => {
    if (!confirm('Are you sure you want to remove this friend?')) {
      return;
    }

    setActionError(null);
    try {
      await removeFriend(friendshipId);
      setFriends((prev) => prev.filter((f) => f.friendship_id !== friendshipId));
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to remove friend');
    }
  };

  if (loading) {
    return (
      <div className="friends-page">
        <div className="loading">Loading friends...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="friends-page">
        <div className="error-message">{error}</div>
        <button onClick={loadData}>Try Again</button>
      </div>
    );
  }

  return (
    <div className="friends-page">
      <div className="page-header">
        <div className="header-content">
          <Link to="/" className="back-link">
            &larr; Back to Playlists
          </Link>
          <h1>Friends</h1>
          <p className="page-description">Add friends to share playlists with them</p>
        </div>
      </div>

      {!hasUsername && (
        <div className="username-prompt">
          <p>You need a username to add friends and share playlists.</p>
          <button onClick={() => setShowUsernameModal(true)}>Set Username</button>
        </div>
      )}

      {actionError && <div className="error-message">{actionError}</div>}

      {/* Search for users */}
      <section className="friends-section">
        <h2>Find Friends</h2>
        <div className="search-container">
          <input
            type="text"
            placeholder="Search by username..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            disabled={!hasUsername}
          />
          {searching && <span className="search-status">Searching...</span>}
        </div>

        {searchResults.length > 0 && (
          <ul className="user-list">
            {searchResults.map((user) => (
              <li key={user.id} className="user-item">
                <div className="user-info">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="user-avatar" />
                  ) : (
                    <div className="user-avatar placeholder">
                      {user.username?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                  <div className="user-details">
                    <span className="user-username">@{user.username}</span>
                    {user.name && <span className="user-name">{user.name}</span>}
                  </div>
                </div>
                <button className="small" onClick={() => handleSendRequest(user.username!)}>
                  Add Friend
                </button>
              </li>
            ))}
          </ul>
        )}

        {searchQuery && !searching && searchResults.length === 0 && (
          <p className="no-results">No users found matching "{searchQuery}"</p>
        )}
      </section>

      {/* Friend Requests */}
      {requests.length > 0 && (
        <section className="friends-section">
          <h2>Friend Requests ({requests.length})</h2>
          <ul className="user-list">
            {requests.map((request) => (
              <li key={request.id} className="user-item">
                <div className="user-info">
                  {request.from_user.picture ? (
                    <img src={request.from_user.picture} alt="" className="user-avatar" />
                  ) : (
                    <div className="user-avatar placeholder">
                      {request.from_user.username?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                  <div className="user-details">
                    <span className="user-username">@{request.from_user.username}</span>
                    {request.from_user.name && (
                      <span className="user-name">{request.from_user.name}</span>
                    )}
                  </div>
                </div>
                <div className="user-actions">
                  <button className="small" onClick={() => handleAcceptRequest(request.id)}>
                    Accept
                  </button>
                  <button
                    className="small secondary"
                    onClick={() => handleRejectRequest(request.id)}
                  >
                    Decline
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Friends List */}
      <section className="friends-section">
        <h2>Your Friends ({friends.length})</h2>
        {friends.length === 0 ? (
          <p className="empty-state">
            You haven't added any friends yet. Search for users above to add them!
          </p>
        ) : (
          <ul className="user-list">
            {friends.map((friend) => (
              <li key={friend.id} className="user-item">
                <Link to={`/friends/${friend.id}/stats`} className="user-info user-info-link">
                  {friend.picture ? (
                    <img src={friend.picture} alt="" className="user-avatar" />
                  ) : (
                    <div className="user-avatar placeholder">
                      {friend.username?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                  <div className="user-details">
                    <span className="user-username">@{friend.username}</span>
                    {friend.name && <span className="user-name">{friend.name}</span>}
                  </div>
                </Link>
                <button
                  className="small secondary danger"
                  onClick={() => handleRemoveFriend(friend.friendship_id)}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <UsernameSetupModal
        isOpen={showUsernameModal}
        onClose={() => setShowUsernameModal(false)}
        onSuccess={() => setShowUsernameModal(false)}
      />
    </div>
  );
}
